import { AxiosPromise } from 'axios';
import { http } from 'services';

// A question pending manual grading, as returned inside a pending attempt.
export interface PendingQuestion {
  _id?: string;
  id?: string;
  prompt?: string;
  points?: number;
  type?: string;
  correctAnswer?: string | string[] | Record<string, unknown>;
  explanation?: string;
  /** IELTS-style Writing: graded with criterion bands instead of points. */
  writingTask?: 'TASK_1' | 'TASK_2';
}

export interface PendingAnswer {
  id: string;
  value: string | string[] | Record<string, unknown>;
  question: PendingQuestion;
  aiScore?: number;
  aiConfidence?: 'HIGH' | 'MEDIUM' | 'LOW';
  aiReasoning?: string;
  aiBandCriteria?: Array<{ key: string; band: number }>;
  /** IELTS Speaking: the stored answers with short-lived links, and the AI's transcript once made. */
  speakingRecordings?: Array<{
    key: string;
    part: number;
    prompt: string;
    durationSec: number | null;
    url: string | null;
    transcript?: string | null;
  }>;
  /** IELTS Speaking: the platform lets the AI suggest bands for recordings. */
  aiAvailable?: boolean;
}

export interface PendingAttempt {
  id: string;
  candidate: { id: string; name: string; email: string } | null;
  // Set instead of `candidate` when the org enables anonymous (blind) grading;
  // a stable per-attempt pseudonym code. The client renders "Respondent <label>".
  anonymousLabel?: string;
  test: { id: string; title: string } | null;
  answers: PendingAnswer[];
}

export interface GradeManualData {
  answerId: string;
  score: number;
  notes?: string;
}

export interface AiGradeSuggestion {
  suggestion: true;
  answerId: string;
  questionId: string;
  maxPoints: number;
  suggestedScore: number;
  confidence?: 'HIGH' | 'MEDIUM' | 'LOW';
  rationale: string;
}

// One flat row per manually-graded answer, as returned by the export endpoint.
export interface GradedExportRow {
  candidateName: string;
  candidateEmail: string;
  test: string;
  question: string;
  questionType: string;
  score: number;
  maxPoints: number;
  grader: string;
  gradedAt: string | null;
  note: string;
}

// Org context is carried by the auto-injected X-Organization-Id header
// (bootstrap.ts) — the SAME header the server resolves + permission-checks
// against. We intentionally do NOT pass org as a query param (a client-supplied
// org decoupled from the checked one is a confused-deputy risk).
export const GetPending = (): AxiosPromise<PendingAttempt[]> =>
  http.request.get('/api/grading/pending');

// Export manually-graded answers (org-scoped). Gated by GRADING_EXPORT server-side.
export const GetGradedExport = (): AxiosPromise<GradedExportRow[]> =>
  http.request.get('/api/grading/export');

export const GradeManual = (data: GradeManualData): AxiosPromise =>
  http.request.post('/api/grading/manual', data);

export const AiSuggest = (
  attemptId: string,
  questionId: string,
  language: string,
): AxiosPromise<AiGradeSuggestion> =>
  http.request.post(`/api/grading/${attemptId}/ai/${questionId}`, { language });
